// Section Rendering resolves a single option tuple. It never claims a complete variant matrix.
window.AplovioProductOptions = class {
  constructor(owner) {
    this.owner = owner;
    this.contextIds = owner.controls.map(control => control.selectedOptions[0]?.dataset.optionValueId);
  }

  cancel(silent = false) {
    const pending = !!this.request;
    if (this.request) { this.owner.optionPending = false; this.owner.optionFailure = true; }
    this.request?.abort();
    this.request = null;
    if(pending && !silent && this.owner.isConnected){
      if(this.owner.refreshNotice)this.owner.refreshNotice.hidden=false;
      this.owner.updatePurchase(this.owner.variant.selectedOptions[0]);
    }
  }

  async select({ timeout = 10000, accept = () => true, keepSizeCleared = false } = {}) {
    const owner = this.owner;
    this.cancel(true);
    owner.cancelNativeRefresh();
    const ids = owner.controls.map((control, index) => control.selectedOptions[0]?.dataset.optionValueId || (keepSizeCleared && control === owner.sizeControl ? this.contextIds[index] : null));
    const values = owner.controls.map(control => control.value);
    const request = new AbortController();
    this.request = request;
    const epoch = owner.selectionEpoch = (owner.selectionEpoch || 0) + 1;
    const stockGeneration=owner.stockGeneration,stockRevision=owner.stockRevision;
    owner.optionPending = true;
    owner.optionFailure = false;
    owner.variant.disabled = true;
    owner.updatePurchase(owner.variant.selectedOptions[0]);
    const notice = owner.refreshNotice;
    if (notice) notice.hidden = true;
    const timer = setTimeout(() => request.abort('timeout'), timeout);
    let committed = false;
    const current = () => owner.isConnected && !request.signal.aborted && this.request === request && owner.selectionEpoch === epoch && owner.controls.every((control, index) => control.value === values[index]) && accept();
    try {
      if (!ids.length || ids.some(id => !/^[1-9]\d{0,24}$/.test(id || '')) || !/^[\w-]+$/.test(owner.dataset.sectionId || '')) throw new Error('option-context');
      const url = new URL(owner.dataset.productUrl, location.href);
      if (url.origin !== location.origin || !/\/products\/[^/]+\/?$/.test(url.pathname)) throw new Error('option-origin');
      url.search = '';
      url.searchParams.set('option_values', ids.join(','));
      if (notice) {
        const fallback=new URL(url);
        if(keepSizeCleared){
          fallback.searchParams.set('aplovio_size','required');
          fallback.searchParams.set('aplovio_options',JSON.stringify({productGid:owner.dataset.productGid,selections:owner.controls.flatMap((control,index)=>control===owner.sizeControl?[]:[{position:index+1,value:control.value,valueId:control.selectedOptions[0]?.dataset.optionValueId||null}])}));
        }
        notice.querySelector('a').href=fallback.href;
      }
      const {controls, selected} = await this.readSelection(url, values, ids, request.signal);
      if (!current()) return false;
      owner.controls.forEach((control, index) => {
        control.replaceChildren(...[...controls[index].options].map(option => owner.copyNativeOption(option)));
        control.value = controls[index].value;
      });
      this.contextIds = ids;
      owner.variant.replaceChildren(...(selected ? [owner.copyNativeOption(selected)] : []));
      owner.variant.selectedIndex = selected ? 0 : -1;
      owner.rows = [...owner.variant.options].map(option => ({ option, values: JSON.parse(option.dataset.options) }));
      if(selected?.dataset.available==='true' && owner.stockGeneration===stockGeneration && owner.stockRevision===stockRevision)owner.stockUnavailable?.delete(owner.stockVariantKey(owner.variant.options[0]));
      owner.optionPending = false;
      owner.optionFailure = false;
      committed = true;
      owner.variant.disabled = !selected;
      owner.sizeCleared = keepSizeCleared;
      if (keepSizeCleared) { owner.sizeControl.selectedIndex = -1; owner.variant.selectedIndex = -1; owner.variant.disabled = true; }
      if (owner.sizeChoices) owner.renderSizeChoices();
      owner.availability();
      owner.update(owner.variant.selectedOptions[0]);
      return !!selected && !keepSizeCleared;
    } catch (error) {
      // Cancellation still leaves the unconfirmed intent unpurchasable. A newer request owns its own UI.
      if (this.request === request && owner.isConnected && owner.selectionEpoch === epoch) {
        owner.optionFailure = true;
        if (notice) notice.hidden = false;
        owner.dispatchEvent(new CustomEvent('aplovio:option-error', { bubbles: true, detail: { reason: request.signal.aborted ? 'cancelled' : error.message } }));
      }
      return false;
    } finally {
      clearTimeout(timer);
      if (this.request === request) {
        this.request = null;
        owner.optionPending = false;
        // accept() may have invalidated a response without an exception (guide close or Back).
        if (!committed) { owner.optionFailure = true; if(notice)notice.hidden=false; }
        if (owner.isConnected) owner.updatePurchase(owner.variant.selectedOptions[0]);
      }
    }
  }
  async preview(values, {signal} = {}) {
    const owner = this.owner;
    const ids = owner.controls.map((control, index) =>
      [...control.options].find(option => option.value === values[index])?.dataset.optionValueId);
    if (ids.some(id => !/^[1-9]\d{0,24}$/.test(id || ''))) throw new Error('option-context');
    const url = new URL(owner.dataset.productUrl, location.href);
    if (url.origin !== location.origin || !/\/products\/[^/]+\/?$/.test(url.pathname)) throw new Error('option-origin');
    url.search = '';
    url.searchParams.set('option_values', ids.join(','));
    return this.readSelection(url, values, ids, signal);
  }

  async readSelection(url, values, ids, signal) {
    const owner = this.owner;
    url.searchParams.set('section_id', owner.dataset.sectionId);
    const response = await window.AplovioLazy.read(url, { signal: signal, credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' } });
    if (!response.ok || !response.headers.get('content-type')?.includes('text/html')) throw new Error('option-response');
    const destination=new URL(response.url);
    if(destination.origin!==url.origin || destination.pathname!==url.pathname)throw new Error('option-redirect');
    const reader = response.body.getReader(), decoder = new TextDecoder();
    let html = '', bytes = 0;
    while (true) {
      const chunk = await reader.read();
      if (chunk.done) break;
      bytes += chunk.value.byteLength;
      if (bytes > 1000000) { await reader.cancel(); throw new Error('option-size'); }
      html += decoder.decode(chunk.value, { stream: true });
    }
    html += decoder.decode();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const products = doc.querySelectorAll('aplovio-product'), fresh = products[0];
    if (products.length !== 1 || ['productGid', 'sectionId', 'country', 'language'].some(key => fresh.dataset[key] !== owner.dataset[key]) || fresh.dataset.optionResolution !== 'native') throw new Error('option-owner');
    const controls = [...fresh.querySelectorAll('[data-option-index]')], variants = fresh.querySelector('[data-variant-select]');
    if (controls.length !== owner.controls.length || !variants || variants.options.length > 1) throw new Error('option-structure');
    for (let index = 0; index < controls.length; index++) {
      const control = controls[index];
      if (control.dataset.optionIndex !== String(index) || control.hasAttribute('data-size-option') !== owner.controls[index].hasAttribute('data-size-option') || control.selectedOptions[0]?.dataset.optionValueId !== ids[index]) throw new Error('option-tuple');
      const optionIds = [...control.options].map(option => option.dataset.optionValueId);
      if (optionIds.some(id => !/^[1-9]\d{0,24}$/.test(id || '')) || new Set(optionIds).size !== optionIds.length) throw new Error('option-values');
    }
    const selected = variants.options[0];
    if (selected) {
      const tuple = JSON.parse(selected.dataset.options || 'null');
      if (!/^[1-9]\d{0,24}$/.test(selected.value) || fresh.dataset.selectedVariant !== selected.value || !Array.isArray(tuple) || tuple.length !== controls.length || tuple.some((value, index) => value !== controls[index].value) || !['true', 'false'].includes(selected.dataset.available) || typeof selected.dataset.price !== 'string' || selected.dataset.price.length > 200) throw new Error('option-variant');
    } else if (fresh.dataset.selectedVariant) throw new Error('option-null');
    return {controls, selected};
  }

};
